/**
 * Properties Store
 * State management for element properties inspection
 */

import { create } from 'zustand';

export interface PropertyValue {
  displayName: string;
  displayValue: any;
  displayCategory?: string;
  units?: string;
  type?: number;
  hidden?: boolean;
}

export interface PropertiesState {
  selectedDbId: number | null;
  selectedElementName: string;
  properties: PropertyValue[];
  propertiesGrouped: Map<string, PropertyValue[]>;
  isLoading: boolean;
  searchFilter: string;
  expandedCategories: Set<string>;
  showHiddenProperties: boolean;
  
  // Actions
  setSelectedElement: (dbId: number | null, name?: string) => void;
  setProperties: (properties: PropertyValue[]) => void;
  setIsLoading: (isLoading: boolean) => void;
  setSearchFilter: (filter: string) => void;
  toggleCategory: (category: string) => void;
  toggleShowHidden: () => void;
  clearSelection: () => void;
}

export const usePropertiesStore = create<PropertiesState>((set) => ({
  selectedDbId: null,
  selectedElementName: '',
  properties: [],
  propertiesGrouped: new Map(),
  isLoading: false,
  searchFilter: '',
  expandedCategories: new Set(['Dimensions', 'Identity Data', 'Constraints']),
  showHiddenProperties: false,

  setSelectedElement: (dbId, name = '') => set({ 
    selectedDbId: dbId, 
    selectedElementName: name 
  }),

  setProperties: (properties) => {
    // Group properties by category
    const grouped = new Map<string, PropertyValue[]>();
    
    properties.forEach(prop => {
      const category = prop.displayCategory || 'Other';
      if (!grouped.has(category)) {
        grouped.set(category, []);
      }
      grouped.get(category)!.push(prop);
    });

    // Sort categories and properties
    const sortedGrouped = new Map(
      Array.from(grouped.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([cat, props]) => [
          cat,
          props.sort((a, b) => a.displayName.localeCompare(b.displayName))
        ])
    );

    set({ 
      properties, 
      propertiesGrouped: sortedGrouped 
    });
  },

  setIsLoading: (isLoading) => set({ isLoading }),

  setSearchFilter: (filter) => set({ searchFilter: filter }),

  toggleCategory: (category) => set((state) => {
    const expanded = new Set(state.expandedCategories);
    if (expanded.has(category)) {
      expanded.delete(category);
    } else {
      expanded.add(category);
    }
    return { expandedCategories: expanded };
  }),

  toggleShowHidden: () => set((state) => ({ 
    showHiddenProperties: !state.showHiddenProperties 
  })),

  clearSelection: () => set({
    selectedDbId: null,
    selectedElementName: '',
    properties: [],
    propertiesGrouped: new Map(),
    searchFilter: ''
  })
}));